import {match, P} from 'ts-pattern'
import {O} from 'ts-toolbelt'
import {z} from 'zod'
import {zBounds, zAmountType, zHardBounds, zBoundsVersion} from './question'
import {zx} from '../type'

export const forecastTypes = ['logistic', 'likelihood', 'drawn'] as const
export type ForecastType = (typeof forecastTypes)[number]
export type QuantityForecastType = 'logistic' | 'drawn'
export type ForecastLogistic = z.infer<(typeof zForecastTypes)['logistic']>
export type ForecastLikelihood = z.infer<(typeof zForecastTypes)['likelihood']>
export type ForecastDrawn = z.infer<(typeof zForecastTypes)['drawn']>
export type ForecastCfg = z.infer<typeof zForecast>

export const forecastStatuses = ['expired', 'excluded'] as const
export type ForecastStatus = (typeof forecastStatuses)[number]

export const forecastStatusActions = [...forecastStatuses, 'restored'] as const
export type ForecastStatusAction = (typeof forecastStatusActions)[number]

export const zForecastCfgStatus = z
  .record(z.enum(forecastStatuses), z.object({at: z.string(), by: z.string()}))
  .optional()
  .default({})
const zForecastCommon = z.object({
  deleted: z.boolean().optional(),
  notes: z.preprocess(
    v =>
      match(v)
        .with(P.string, s => (s.trim().length == 0 ? undefined : s)) // blank text = undefined so we don't have two paths for the UI
        .otherwise(() => v),
    z.string().optional()
  ),
  /** subquestions have multiple forecasts required */
  code: z.string().optional(),
  status: zForecastCfgStatus
})

const zPdPoint = z.object({
  id: z.string(),
  p: z.number(),
  x: z.number()
})

/** For a soft bound, this just means the percent from the tail point.
 * For a hard bound logistic, this means the pct inside the bound itself */
export const zAmountTail = z.object({
  pct: zx.nullish.num.default(0)
})

export type AmountTail = z.infer<typeof zAmountTail>

const amountCommonShape = {
  bounds: zBounds.default({}),
  /** hard bounds used to be set on the forecast and need to be here for backwards compatibility. But the new method is to never set this inherit the question's */
  hardBounds: zHardBounds.optional(),
  amountType: zAmountType.default('number'),
  location: zx.nullish.num,
  skew: zx.nullish.num.default(0),
  scale: zx.nullish.num,
  cumulative: z.boolean().default(false),
  tails: z
    .object({
      left: zAmountTail.optional(),
      right: zAmountTail.optional()
    })
    .default({}),
  boundsVersion: zBoundsVersion
}

export const zForecastTypes = {
  logistic: zForecastCommon.extend({
    type: z.literal('logistic'),
    ...amountCommonShape
  }),
  drawn: zForecastCommon.extend({
    type: z.literal('drawn'),
    ...amountCommonShape, // we keep the logistic in case we switch back. cumulative applies to both
    points: z.array(zPdPoint).default([])
  }),
  likelihood: zForecastCommon.extend({
    type: z.literal('likelihood'),
    likelihood: zx.nullish.num,
    relativePoints: zx.nullish.num
  })
}

export type ForecastLogisticValid = O.Required<ForecastLogistic, 'location'>
export const isLogisticValid = (cfg: ForecastCfg): cfg is ForecastLogisticValid =>
  cfg.type == 'logistic' && cfg.location != null

export type ForecastLikelihoodValid = O.Required<ForecastLikelihood, 'likelihood'>
export const isLikelihoodValid = (cfg: ForecastCfg): cfg is ForecastLikelihoodValid =>
  cfg.type == 'likelihood' && cfg.likelihood != null

// similar to question.upgradeBoundsVersion, but for forecasts
const upgradeForecastBounds = (o: any) => {
  return match(o)
    .when(
      o => !('boundsVersion' in o) || o.boundsVersion == '1',
      o => {
        // if the vounds version is 1, then use the v1 schema to parse it and then convert it to v2
        const zBound = z.object({value: z.number(), hard: z.boolean().optional()})
        const {bounds: boundsV1} = z
          .object({
            bounds: z.object({min: zBound.default({value: 0}), max: zBound.default({value: 100})}).default({})
          })
          .parse(o)

        const bounds = {
          min: boundsV1.min.value,
          max: boundsV1.max.value
        }
        const hardBounds = {
          min: boundsV1.min.hard ? boundsV1.min.value : undefined,
          max: boundsV1.max.hard ? boundsV1.max.value : undefined
        }
        return {...o, bounds, hardBounds, boundsVersion: '2'}
      }
    )
    .otherwise(() => o)
}

export const zForecast = z.preprocess(
  upgradeForecastBounds,
  z.discriminatedUnion('type', [zForecastTypes.logistic, zForecastTypes.likelihood, zForecastTypes.drawn])
)

export const zForecastFetchedBase = z.object({
  id: z.string(),
  cfg: zForecast,
  question_id: z.string(),
  subquestion_id: zx.nullish.string,
  created_by: z.string().optional(),
  created_at: z.string().optional(),
  ts: zx.nullish.num
})
export type ForecastFetchedBase = z.infer<typeof zForecastFetchedBase>
